/**
 * The one enrollment flow for two-factor (T2), used in both places it can
 * start: Settings → Security, and the login page when the account requires
 * 2FA of a sign-in that has none (forced enrollment).
 *
 * Three stages: the QR (with the secret beside it for typing by hand), the
 * proving code, and the recovery codes — which the server hands out exactly
 * once, so the flow does not finish until the person says they kept them.
 */
import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { twoFactorEnableApi, twoFactorSetupApi } from '../api/admin';
import { CodeBlock } from './fs';

export default function TwoFactorEnroll({ onDone }: { onDone: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    twoFactorSetupApi()
      .then((r) => setSetup({ secret: r.secret, uri: r.uri }))
      .catch((e: any) => setError(e.message || 'Could not start two-factor setup.'));
  }, []);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el || !setup) return;
    QRCode.toCanvas(el, setup.uri, { width: 176, margin: 1 }).catch(() => undefined);
  }, [setup]);

  async function prove(e?: React.FormEvent) {
    e?.preventDefault();
    if (!code.trim()) return;
    setError(''); setBusy(true);
    try {
      const r = await twoFactorEnableApi(code.trim());
      setRecovery(r.recoveryCodes);
    } catch (err: any) {
      setError(err.message || 'That code did not match.');
    } finally {
      setBusy(false);
    }
  }

  if (recovery) {
    return (
      <div>
        <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          Two-factor is on. These recovery codes are shown <b>only this once</b> — each one signs
          you in a single time if you lose your phone. Keep them somewhere safe.
        </p>
        {/* Copy puts every code on the clipboard, one per line (R10). */}
        <CodeBlock code={recovery.join('\n')} label="Recovery codes" className="tf-recovery">
          {recovery.map((c) => <div key={c}>{c}</div>)}
        </CodeBlock>
        <button className="fs-btn primary" onClick={onDone}>I saved my recovery codes</button>
      </div>
    );
  }

  return (
    <form onSubmit={prove}>
      {error && <div className="ad-err">{error}</div>}
      <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
        Scan this with an authenticator app (Google Authenticator, 1Password, Authy — anything
        that speaks TOTP), then enter the six-digit code the app shows.
      </p>
      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap', margin: '12px 0' }}>
        <canvas ref={canvasRef} width={176} height={176} style={{ borderRadius: 8, border: '1px solid var(--border)' }} />
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, minWidth: 200, flex: 1 }}>
          Can’t scan? Enter this key by hand:
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-900)', wordBreak: 'break-all', marginTop: 6 }}>
            {setup ? setup.secret : 'Loading…'}
          </div>
        </div>
      </div>
      <div className="ad-field">
        <label htmlFor="totp-code">Code from your app</label>
        <input
          id="totp-code" inputMode="numeric" autoComplete="one-time-code" value={code}
          onChange={(e) => setCode(e.target.value)} placeholder="123 456" autoFocus
        />
      </div>
      <button className="fs-btn primary" type="submit" disabled={busy || !setup}>
        {busy ? 'Checking…' : 'Turn on two-factor'}
      </button>
    </form>
  );
}
